import { supabase } from '@/integrations/supabase/client';

export interface RiskProfile {
  id: string;
  user_id: string;
  account_size: number | null;
  risk_per_trade_pct: number | null;
  max_daily_loss_pct: number | null;
  max_weekly_loss_pct: number | null;
  max_open_positions: number | null;
  allowed_markets: string[] | null;
  trading_style: string | null;
  timezone_preference: string | null;
  session_preference: string | null;
  created_at: string;
  updated_at: string;
}

export interface RiskProfileInput {
  account_size?: number;
  risk_per_trade_pct?: number;
  max_daily_loss_pct?: number;
  max_weekly_loss_pct?: number | null;
  max_open_positions?: number;
  allowed_markets?: string[];
  trading_style?: string;
  timezone_preference?: string | null;
  session_preference?: string | null;
}

// Fetch current user's risk profile
export const getRiskProfile = async (userId: string): Promise<RiskProfile | null> => {
  const { data, error } = await supabase
    .from('risk_profiles')
    .select('*')
    .eq('user_id', userId)
    .maybeSingle();

  if (error) {
    console.error('Error fetching risk profile:', error);
    throw error;
  }

  return data;
};

// Create or update risk profile (upsert)
export const upsertRiskProfile = async (
  userId: string,
  profile: RiskProfileInput
): Promise<RiskProfile> => {
  const { data, error } = await supabase
    .from('risk_profiles')
    .upsert(
      {
        user_id: userId,
        ...profile,
        updated_at: new Date().toISOString()
      },
      { onConflict: 'user_id' }
    )
    .select()
    .single();

  if (error) {
    console.error('Error upserting risk profile:', error);
    throw error;
  }

  return data;
};

// Calculate risk amount in USD based on profile
export const calculateRiskAmount = (profile: RiskProfile | null): number => {
  // If profile is not configured, do NOT assume any defaults
  if (!profile || profile.account_size == null || profile.risk_per_trade_pct == null) {
    return 0;
  }

  const accountSize = profile.account_size;
  const riskPct = profile.risk_per_trade_pct;
  return (accountSize * riskPct) / 100;
};

// Calculate max daily loss in USD
export const calculateMaxDailyLoss = (profile: RiskProfile | null): number => {
  // If profile is not configured, do NOT assume any defaults
  if (!profile || profile.account_size == null || profile.max_daily_loss_pct == null) {
    return 0;
  }

  const accountSize = profile.account_size;
  const maxDailyPct = profile.max_daily_loss_pct;
  return (accountSize * maxDailyPct) / 100;
};

// Build context string for AI prompts
export const buildRiskProfileContext = (profile: RiskProfile | null): string => {
  if (!profile) {
    return 'Risk Profile: Not configured. Please set it in your dashboard.';
  }

  const accountSize = profile.account_size ?? 0;
  const riskPct = profile.risk_per_trade_pct ?? 0;
  const maxDailyPct = profile.max_daily_loss_pct ?? 0;
  const riskAmount = calculateRiskAmount(profile);
  const maxDailyLoss = calculateMaxDailyLoss(profile);

  return `Risk Profile:
- Account Size: $${accountSize.toLocaleString()}
- Risk Per Trade: ${riskPct}% ($${riskAmount.toLocaleString()})
- Max Daily Loss: ${maxDailyPct}% ($${maxDailyLoss.toLocaleString()})
- Max Open Positions: ${profile.max_open_positions || 3}
- Trading Style: ${profile.trading_style || 'swing'}
- Session Preference: ${profile.session_preference || 'any'}
- Allowed Markets: ${(profile.allowed_markets || []).join(', ')}`;
};
